import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";

import { SiteNav, SiteFooter } from "../../components/fragments/ProductChrome";
import { useStaticSeo } from "../../hooks/useSeo";
import { PRODUCT_SEO } from "../../data/seo";
import { PRODUCT_ORIGIN } from "../../data/brand";
import * as account from "../../data/accountApi";
import SavedProperties from "../../components/fragments/SavedProperties";
import useGoogleSignIn from "../../hooks/useGoogleSignIn";

/*
 * Signing up, signing in, confirming an address and resetting a password.
 *
 * ONE FILE, FIVE SCREENS, because they are one flow and they share
 * everything: the card, the field, the error line, the wording. Five files
 * would be five copies of the same form drifting apart, and the whole thing
 * is smaller than the property report's own header.
 *
 * NONE OF THEM ARE INDEXED. Every entry below carries `noindex`. A sign-in
 * page in search results is a phishing target and a support call; nothing
 * here is content and none of it should compete with the pages that are.
 *
 * PRODUCT ONLY. These live at doorstepdata.co.uk, where accounts exist. The
 * agency site has no customers and its nginx block does not route /account
 * anyway - but the chrome comes from ProductChrome, which falls back to the
 * agency's nav if somebody reaches one of these on the wrong host, so it
 * would at least look deliberate rather than broken.
 */

const Wrap = styled.div`
    max-width: 26rem;
    margin: 0 auto;
    padding: 2.5rem 1.25rem 4rem;
`;
const Card = styled.div`
    border: 1px solid ${(props) => props.theme.lightgrey};
    border-radius: 10px;
    background: #fff;
    padding: 1.6rem 1.4rem;
`;
const Head = styled.h1`
    margin: 0 0 0.4rem;
    font-size: 1.25rem;
    color: ${(props) => props.theme.darkblue};
`;
const Blurb = styled.p`
    margin: 0 0 1.3rem;
    font-size: 0.92rem;
    line-height: 1.55;
    color: ${(props) => props.theme.black};
`;
const Field = styled.label`
    display: block;
    margin-bottom: 1rem;
    span {
        display: block;
        font-size: 0.8rem;
        font-weight: 600;
        color: ${(props) => props.theme.darkblue};
        margin-bottom: 0.35rem;
    }
    input {
        width: 100%;
        box-sizing: border-box;
        padding: 0.7rem 0.8rem;
        font-size: 1rem;
        font-family: inherit;
        border: 1px solid ${(props) => props.theme.lightgrey};
        border-radius: ${(props) => props.theme.radius};
        background: #fff;
        &:focus {
            outline: 2px solid ${(props) => props.theme.lightblue};
            outline-offset: 1px;
            border-color: ${(props) => props.theme.darkblue};
        }
    }
`;
const Hint = styled.span`
    display: block;
    font-size: 0.78rem;
    font-weight: 400 !important;
    color: ${(props) => props.theme.grey};
    margin: 0.35rem 0 0 !important;
`;
const Go = styled.button`
    width: 100%;
    padding: 0.8rem 1rem;
    font-size: 1rem;
    font-weight: 600;
    font-family: inherit;
    color: #fff;
    background: ${(props) => props.theme.darkblue};
    border: 0;
    border-radius: ${(props) => props.theme.radius};
    cursor: pointer;
    &:disabled {
        opacity: 0.55;
        cursor: default;
    }
`;
const Err = styled.p`
    margin: 0 0 1rem;
    padding: 0.7rem 0.8rem;
    font-size: 0.88rem;
    line-height: 1.5;
    color: #7a2318;
    background: #fdf1ef;
    border: 1px solid #f3cfc8;
    border-radius: ${(props) => props.theme.radius};
`;
const Done = styled.p`
    margin: 0 0 1rem;
    padding: 0.7rem 0.8rem;
    font-size: 0.9rem;
    line-height: 1.5;
    color: #1d4a22;
    background: #f1f8f1;
    border: 1px solid #cfe5cf;
    border-radius: ${(props) => props.theme.radius};
`;
const Alt = styled.p`
    margin: 1.2rem 0 0;
    font-size: 0.88rem;
    line-height: 1.6;
    color: ${(props) => props.theme.grey};
    a {
        color: ${(props) => props.theme.darkblue};
        font-weight: 600;
    }
`;
const Rows = styled.dl`
    margin: 0 0 1.4rem;
    display: grid;
    gap: 0.5rem;
    dt {
        font-size: 0.78rem;
        font-weight: 600;
        color: ${(props) => props.theme.grey};
    }
    dd {
        margin: 0 0 0.6rem;
        font-size: 0.95rem;
        color: ${(props) => props.theme.black};
        word-break: break-word;
    }
`;

const Or = styled.div`
    display: flex;
    align-items: center;
    gap: 0.7rem;
    margin: 1.1rem 0;
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${(props) => props.theme.grey};

    &::before,
    &::after {
        content: "";
        flex: 1;
        height: 1px;
        background: rgba(15, 76, 92, 0.18);
    }
`;

const DeviceCode = styled.p`
    margin: 1rem 0;
    font-family: "SFMono-Regular", Consolas, "Liberation Mono", monospace;
    font-size: 1.8rem;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-align: center;
    color: ${(props) => props.theme.darkblue};
`;

const GoogleHost = styled.div`
    display: flex;
    justify-content: center;
    min-height: 44px;
`;

/*
 * The shell every screen sits in, so the chrome is written once.
 *
 * Exported because the checkout screens sit in the same chrome and under the
 * same /account paths. Two copies of it would drift the moment one of them
 * gained a header.
 */
export function Shell({ children }) {
    return (
        <>
            <SiteNav />
            <Wrap>
                <Card>{children}</Card>
            </Wrap>
            <SiteFooter />
        </>
    );
}

/**
 * Where to go after signing in, from ?next=, or null.
 *
 * ONLY A PATH ON THIS SITE, and the checks are the point rather than
 * pedantry. A sign-in page that forwards to anything it is handed is an open
 * redirect, which is a phishing primitive: a link to OUR domain that lands
 * on somebody else's, with our name in the part people read.
 *
 * So it must start with a single slash - "//evil.example" is a
 * protocol-relative URL that a browser treats as another origin - must not
 * carry a scheme or a backslash, and must be one of the paths we actually
 * send people to.
 */
function safeNext(search) {
    let value;
    try {
        value = new URLSearchParams(search).get("next");
    } catch (err) {
        return null;
    }
    if (!value) return null;
    if (value[0] !== "/" || value[1] === "/" || value[1] === "\\") return null;
    if (value.indexOf(":") !== -1) return null;
    return /^\/(account|property-report|house-prices)(\/|$)/.test(value) ? value : null;
}

/** The token from ?token=..., or null. */
function tokenFromQuery(search) {
    try {
        const value = new URLSearchParams(search).get("token");
        return value && value.trim() ? value.trim() : null;
    } catch (err) {
        return null;
    }
}

/** "A" or "An", agreeing with whatever the device calls itself. */
function article(word) {
    return /^[aeiou]/i.test(String(word || "").trim()) ? "An" : "A";
}

/* --------------------------------------------------- letting a phone in */

/**
 * Where the app sends somebody: /account/device?code=XXXX-XXXX
 *
 * THE CODE IS SHOWN, AND THAT IS THE SECURITY. This page approves whatever
 * code its URL names, so a link sent by somebody else would otherwise
 * attach THEIR phone to this account - the person would be approving a
 * sign-in they never started. Showing the code and asking whether it
 * matches the one on the phone makes that visible: a link from elsewhere
 * carries a code their app is not displaying.
 *
 * So the question on screen is not "do you want to sign in" - it is "is
 * this your phone", and that is deliberate.
 */
export function DeviceApprove() {
    useStaticSeo({
        title: "Sign in on your phone | Doorstep Data",
        description: "Let the app sign in to your account.",
        canonical: PRODUCT_ORIGIN + "/account/device",
        noindex: true,
    });

    const location = useLocation();
    const history = useHistory();
    const session = account.currentSession();

    const code = useMemo(() => {
        try {
            return new URLSearchParams(location.search).get("code") || "";
        } catch (err) {
            return "";
        }
    }, [location.search]);

    const [waiting, setWaiting] = useState(null);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(null);

    useEffect(() => {
        let alive = true;
        if (!code) {
            setError("That link is missing its code. Open it from the app again.");
            return undefined;
        }
        account
            .devicePending(code)
            .then((r) => alive && setWaiting(r))
            .catch((e) => alive && setError(e.message));
        return () => {
            alive = false;
        };
    }, [code]);

    /*
     * Sent to sign in first, and brought back here. A phone cannot be let
     * into an account nobody is signed in to.
     */
    useEffect(() => {
        if (!session && code) {
            history.replace(
                "/account/sign-in?next=" +
                    encodeURIComponent("/account/device?code=" + code)
            );
        }
    }, [session, code, history]);

    async function answer(deny) {
        setBusy(true);
        setError(null);
        try {
            await account.deviceApprove(code, session.token, deny);
            setDone(deny ? "denied" : "approved");
        } catch (e) {
            setError(e.message);
        } finally {
            setBusy(false);
        }
    }

    if (!session) return null;

    if (done) {
        return (
            <Shell>
                <Head>{done === "approved" ? "Your phone is signed in" : "Refused"}</Head>
                <Done>
                    {done === "approved"
                        ? "You can put this down and go back to the app."
                        : "Nothing has been shared with that device."}
                </Done>
                <Alt>
                    <Link to="/account">Your account</Link>
                </Alt>
            </Shell>
        );
    }

    return (
        <Shell>
            <Head>Is this your phone?</Head>

            {error && <Err>{error}</Err>}

            {waiting && !waiting.answered && (
                <>
                    <Blurb>
                        {/*
                          * "An Android phone", not "A Android phone". The
                          * label comes from the device and the article has
                          * to agree with it - a vowel check is enough for
                          * the handful of words a device calls itself, and
                          * gets "an iPhone" right too.
                          */}
                        {waiting.label
                            ? article(waiting.label) + " " + waiting.label + " is asking to sign in as "
                            : "A device is asking to sign in as "}
                        <strong>{session.email}</strong>.
                    </Blurb>

                    <DeviceCode>{waiting.userCode}</DeviceCode>

                    <Blurb>
                        Only continue if that code is the one showing on your phone right now.
                        If it is not, somebody else is trying to sign in - refuse it.
                    </Blurb>

                    <Go type="button" disabled={busy} onClick={() => answer(false)}>
                        {busy ? "One moment…" : "Yes, that is my phone"}
                    </Go>
                    <Alt>
                        <button type="button" disabled={busy} onClick={() => answer(true)}>
                            No - refuse it
                        </button>
                    </Alt>
                </>
            )}

            {waiting && waiting.answered && (
                <Blurb>That request has already been answered.</Blurb>
            )}
        </Shell>
    );
}

/* --------------------------------------------------- the emailed link */

/**
 * Where a sign-in link lands: /account/link?token=...
 *
 * SPENDS THE TOKEN ON ARRIVAL and shows only the outcome. There is nothing
 * to confirm and no button to press - the link WAS the decision, taken in
 * the mailbox, and asking again here would be ceremony.
 *
 * It runs once. A link is single-use server-side, so a refresh would
 * otherwise show a failure to somebody who has just succeeded.
 */
export function MagicLink() {
    useStaticSeo(PRODUCT_SEO["/account/sign-in"]);
    const history = useHistory();
    const location = useLocation();
    const [error, setError] = useState(null);
    const spent = useRef(false);

    useEffect(() => {
        if (spent.current) return;
        spent.current = true;

        const token = tokenFromQuery(location.search);
        if (!token) {
            setError("That link is missing its code. Ask for another.");
            return;
        }

        account
            .useMagicLink(token)
            .then((res) => {
                account.saveSession({ token: res.token, email: res.email });
                /*
                 * replace, not push: the URL holds a token that has now been
                 * spent, and leaving it in history invites a back button
                 * onto a dead link.
                 */
                history.replace(safeNext(location.search) || "/account");
            })
            .catch((err) => setError(err.message));
    }, [history, location.search]);

    return (
        <Shell>
            <Head>{error ? "That link did not work" : "Signing you in…"}</Head>
            {error ? (
                <>
                    <Err>{error}</Err>
                    <Alt>
                        <Link to="/account/sign-in">Ask for a new link</Link>
                    </Alt>
                </>
            ) : (
                <Blurb>One moment.</Blurb>
            )}
        </Shell>
    );
}

/* ------------------------------------------------------------- sign in */

export function SignIn() {
    useStaticSeo(PRODUCT_SEO["/account/sign-in"]);
    const history = useHistory();
    /* For ?next=, so somebody sent here from the checkout goes back to it. */
    const location = useLocation();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    /* The link has been asked for, so the form is replaced by the answer. */
    const [sent, setSent] = useState(false);
    /* The password form is hidden until somebody says they want it. */
    const [withPassword, setWithPassword] = useState(false);

    async function submit(e) {
        e.preventDefault();
        setError(null);
        setBusy(true);
        try {
            const res = await account.signIn(email.trim(), password);
            account.saveSession({ token: res.token, email: res.email });
            history.push(safeNext(location.search) || "/account");
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    }

    /*
     * GOOGLE FIRST, THEN A LINK, THEN A PASSWORD, which is the order of how
     * much work each asks of somebody. The password form is last and still
     * there, because people who already have one will look for it - but
     * nobody arriving new has to invent one.
     */
    async function withGoogle(credential) {
        setError(null);
        setBusy(true);
        try {
            const res = await account.signInWithGoogle(credential);
            account.saveSession({ token: res.token, email: res.email });
            history.push(safeNext(location.search) || "/account");
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    }

    const googleId = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";
    const google = useGoogleSignIn(googleId, withGoogle);

    async function sendLink(e) {
        e.preventDefault();
        setError(null);
        setBusy(true);
        try {
            await account.requestMagicLink(email.trim());
            /*
             * ALWAYS THE SAME ANSWER. The server deliberately replies
             * identically whether or not the address has an account, so the
             * screen must not say anything more specific either - "we have
             * sent you a link" for an address with no account would reveal
             * exactly what the endpoint refuses to.
             */
            setSent(true);
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    }

    if (sent) {
        return (
            <Shell>
                <Head>Check your email</Head>
                <Done>If that address has an account, a sign-in link is on its way.</Done>
                <Blurb>
                    The link works once, for fifteen minutes. If it does not arrive, look in
                    your spam folder before asking for another.
                </Blurb>
                <Alt>
                    <button type="button" onClick={() => setSent(false)}>
                        Use a different address
                    </button>
                </Alt>
            </Shell>
        );
    }

    return (
        <Shell>
            <Head>Sign in</Head>
            <Blurb>
                Your saved properties and the reports you have bought, on any device. A
                postcode report still needs no account at all.
            </Blurb>
            {error && <Err>{error}</Err>}

            {googleId && (
                <>
                    <GoogleHost ref={google.ref} />
                    {google.error && <Err>{google.error}</Err>}
                    <Or>or</Or>
                </>
            )}

            <form onSubmit={sendLink}>
                <Field>
                    <span>Email</span>
                    <input
                        id="account-email"
                        type="email"
                        autoComplete="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                    />
                </Field>
                <Go type="submit" disabled={busy || !email.trim()}>
                    {busy ? "Sending…" : "Email me a sign-in link"}
                </Go>
            </form>
            <Hint>No password needed. We send a link that signs you straight in.</Hint>

            {!withPassword && (
                <Alt>
                    <button type="button" onClick={() => setWithPassword(true)}>
                        Use a password instead
                    </button>
                </Alt>
            )}

            {withPassword && (
                <>
                    <Or>or with a password</Or>
                    <form onSubmit={submit}>
                        <Field>
                            <span>Password</span>
                            <input
                                id="account-password"
                                type="password"
                                autoComplete="current-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                            />
                        </Field>
                        <Go type="submit" disabled={busy || !email.trim() || !password}>
                            {busy ? "Signing in…" : "Sign in"}
                        </Go>
                    </form>
                    <Alt>
                        <Link to="/account/forgot">Forgotten your password?</Link>
                    </Alt>
                </>
            )}

            <Alt>
                No account? Signing in with Google or a link makes one.
            </Alt>
        </Shell>
    );
}

/* ------------------------------------------------------------- register */

export function Register() {
    useStaticSeo(PRODUCT_SEO["/account/register"]);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [sent, setSent] = useState(false);

    const localProblem = password ? account.passwordProblem(password) : null;

    async function submit(e) {
        e.preventDefault();
        setError(null);
        if (localProblem) return setError(localProblem);
        setBusy(true);
        try {
            await account.register(email.trim(), password);
            setSent(true);
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    }

    if (sent) {
        return (
            <Shell>
                <Head>Check your inbox</Head>
                {/*
                 * Says the same thing whether or not the address already had
                 * an account - the mailbox gets a different email, and only
                 * its owner sees which. See the note in api/routes/customers.js.
                 */}
                <Done>
                    If that address can receive mail, there is a link in it now. It works for 24
                    hours.
                </Done>
                <Alt>
                    Nothing arrived? Check spam, then{" "}
                    <Link to="/account/register">try again</Link>.
                </Alt>
            </Shell>
        );
    }

    return (
        <Shell>
            <Head>Create an account</Head>
            <Blurb>
                Only so your saved properties can follow you between your phone and this website.
                Reports stay free and need no account.
            </Blurb>
            {error && <Err>{error}</Err>}
            <form onSubmit={submit}>
                <Field>
                    <span>Email</span>
                    <input
                        id="register-email"
                        type="email"
                        autoComplete="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                    />
                </Field>
                <Field>
                    <span>Password</span>
                    <input
                        id="register-password"
                        type="password"
                        autoComplete="new-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                    />
                    {/*
                     * Length, not character classes. "One capital, one number,
                     * one symbol" produces Password1! on every site in the
                     * country; length is what actually costs an attacker.
                     */}
                    <Hint>
                        {localProblem || "At least 12 characters. A short sentence works well."}
                    </Hint>
                </Field>
                <Go type="submit" disabled={busy || !email.trim() || !password}>
                    {busy ? "Creating…" : "Create account"}
                </Go>
            </form>
            <Alt>
                Already have one? <Link to="/account/sign-in">Sign in</Link>.
            </Alt>
        </Shell>
    );
}

/* ------------------------------------------------------------- verify */

export function Verify() {
    useStaticSeo(PRODUCT_SEO["/account/verify"]);
    const location = useLocation();
    const history = useHistory();
    const token = tokenFromQuery(location.search);
    const [state, setState] = useState(token ? "working" : "missing");
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!token) return;
        let live = true;
        account
            .verify(token)
            .then((res) => {
                if (!live) return;
                /*
                 * Verifying signs them in, because the alternative is
                 * confirming an address and immediately being asked for the
                 * password they typed four minutes ago.
                 */
                account.saveSession({ token: res.token, email: res.email });
                setState("done");
            })
            .catch((err) => {
                if (!live) return;
                setError(err.message);
                setState("failed");
            });
        return () => {
            live = false;
        };
    }, [token]);

    if (state === "working") {
        return (
            <Shell>
                <Head>Confirming…</Head>
                <Blurb>One moment.</Blurb>
            </Shell>
        );
    }

    if (state === "done") {
        return (
            <Shell>
                <Head>You&rsquo;re all set</Head>
                <Done>Your address is confirmed and you are signed in.</Done>
                <Go type="button" onClick={() => history.push("/account")}>
                    Go to your account
                </Go>
            </Shell>
        );
    }

    return (
        <Shell>
            <Head>That link did not work</Head>
            <Err>
                {error || "This link is incomplete. Open the one in the email rather than typing it."}
            </Err>
            <Blurb>
                Links last 24 hours and work once. Signing in will send a fresh one.
            </Blurb>
            <Go type="button" onClick={() => history.push("/account/sign-in")}>
                Sign in
            </Go>
        </Shell>
    );
}

/* ------------------------------------------------------------- forgot */

export function Forgot() {
    useStaticSeo(PRODUCT_SEO["/account/forgot"]);
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);

    async function submit(e) {
        e.preventDefault();
        setBusy(true);
        try {
            await account.forgot(email.trim());
        } catch (err) {
            /*
             * Even a failure shows the same screen. Whether an address has an
             * account is exactly what this page must not reveal, and an error
             * that appears only for unknown addresses would reveal it.
             */
        } finally {
            setBusy(false);
            setSent(true);
        }
    }

    if (sent) {
        return (
            <Shell>
                <Head>Check your inbox</Head>
                <Done>If that address has an account, a link is on its way. It lasts an hour.</Done>
                <Alt>
                    <Link to="/account/sign-in">Back to sign in</Link>
                </Alt>
            </Shell>
        );
    }

    return (
        <Shell>
            <Head>Forgotten your password</Head>
            <Blurb>Tell us the address on the account and we&rsquo;ll send a link.</Blurb>
            <form onSubmit={submit}>
                <Field>
                    <span>Email</span>
                    <input
                        id="forgot-email"
                        type="email"
                        autoComplete="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                    />
                </Field>
                <Go type="submit" disabled={busy || !email.trim()}>
                    {busy ? "Sending…" : "Send the link"}
                </Go>
            </form>
            <Alt>
                <Link to="/account/sign-in">Back to sign in</Link>
            </Alt>
        </Shell>
    );
}

/* ------------------------------------------------------------- reset */

export function Reset() {
    useStaticSeo(PRODUCT_SEO["/account/reset"]);
    const location = useLocation();
    const history = useHistory();
    const token = tokenFromQuery(location.search);
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    const localProblem = password ? account.passwordProblem(password) : null;

    async function submit(e) {
        e.preventDefault();
        setError(null);
        if (localProblem) return setError(localProblem);
        setBusy(true);
        try {
            const res = await account.reset(token, password);
            account.saveSession({ token: res.token, email: res.email });
            history.push("/account");
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    }

    if (!token) {
        return (
            <Shell>
                <Head>That link is incomplete</Head>
                <Err>Open the link in the email rather than typing it.</Err>
                <Alt>
                    <Link to="/account/forgot">Send a new one</Link>
                </Alt>
            </Shell>
        );
    }

    return (
        <Shell>
            <Head>Set a new password</Head>
            {/*
                Said plainly, because it is what somebody resetting after a
                scare actually wants to know.
            */}
            <Blurb>
                Everywhere you are currently signed in will be signed out.
            </Blurb>
            {error && <Err>{error}</Err>}
            <form onSubmit={submit}>
                <Field>
                    <span>New password</span>
                    <input
                        id="reset-password"
                        type="password"
                        autoComplete="new-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                    />
                    <Hint>
                        {localProblem || "At least 12 characters. A short sentence works well."}
                    </Hint>
                </Field>
                <Go type="submit" disabled={busy || !password}>
                    {busy ? "Saving…" : "Save and sign in"}
                </Go>
            </form>
        </Shell>
    );
}

/* ------------------------------------------------------------- account */

export function AccountHome() {
    useStaticSeo(PRODUCT_SEO["/account"]);
    const history = useHistory();
    const [session] = useState(() => account.currentSession());
    const [details, setDetails] = useState(null);
    const [checked, setChecked] = useState(false);

    useEffect(() => {
        if (!session) return;
        let live = true;
        account
            .me(session.token)
            .then((res) => {
                if (live) setDetails(res);
            })
            .catch(() => {
                /*
                 * The stored token is no longer accepted - expired, or
                 * withdrawn by a password change elsewhere. Keeping it would
                 * show account screens whose every call fails.
                 */
                if (!live) return;
                account.clearSession();
            })
            .finally(() => {
                if (live) setChecked(true);
            });
        return () => {
            live = false;
        };
    }, [session]);

    if (!session || (checked && !details)) {
        return (
            <Shell>
                <Head>Sign in</Head>
                <Blurb>You are not signed in on this device.</Blurb>
                <Go type="button" onClick={() => history.push("/account/sign-in")}>
                    Sign in
                </Go>
                <Alt>
                    No account yet? <Link to="/account/register">Create one</Link>.
                </Alt>
            </Shell>
        );
    }

    return (
        <Shell>
            <Head>Your account</Head>
            <Rows>
                <dt>Email</dt>
                <dd>{(details && details.email) || session.email}</dd>
                {details && details.createdAt && (
                    <>
                        <dt>Member since</dt>
                        <dd>
                            {new Date(details.createdAt).toLocaleDateString("en-GB", {
                                day: "numeric",
                                month: "long",
                                year: "numeric",
                            })}
                        </dd>
                    </>
                )}
            </Rows>
            {/*
              * This used to read "Nothing is stored with us yet. Saved
              * properties stay on your phone until hosting arrives." That was
              * true and it was also the subscription's main promise going
              * unmet - £19.99 a month sells saved historic data. The hosting
              * arrived on 4 November 2026 and this is it.
              */}
            <SavedProperties token={session.token} />
            <Go
                type="button"
                onClick={() => {
                    account.clearSession();
                    history.push("/account/sign-in");
                }}
            >
                Sign out
            </Go>
            <Alt>
                <Link to="/account/forgot">Change your password</Link>
            </Alt>
        </Shell>
    );
}
